/* System defined core library */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import * as moment from 'moment';

/* User defined library/components */
import { BootstrapParams } from '../../../bootstrap-params';
import { MetricApiService } from '../../scheduling/shared/services/metric-api.service';
import { SelectedExamDetailService } from '../../scheduling/shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../models/scheduling/appointmentdetail.model';

/* User defined enums/constants */
import { TargetFlow, timerBroadCastIntervals } from '../constants/scheduling.constant';
import { LogLevel } from '../enums/scheduling.enum';



@Injectable()
export class TimerService {
  /* User defined private/public variables */
  public timerSpanStart: string = "<span role='timer'>";
  public alertSpanStart: string = "<span aria-live='assertive' role='alert'>";
  public clockIcon: string = "<em class='fa fa - clock - o' aria-hidden='true'></em>";
  public spanEnd: string = "</span>";

  /* Service subscriptions */
  client$: Subscription;
  selectedAppointments$: Subscription;

  seatHoldTime: number;
  selectedAppointments: AppointmentDetail[] = [];

  /* Constructor declaration with required injected services */
  constructor(
    private metricApiService: MetricApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private router: Router,
    private bootstrapParams: BootstrapParams,
    private translate: TranslateService
  ) {
    this.client$ = selectedExamDetailService.client.subscribe(
      client => {
        if (!!client && !!client.code && this.bootstrapParams.longSeatHoldTimeClients.indexOf(client.code.toUpperCase())!==-1) {
          this.seatHoldTime = this.bootstrapParams.longSeatHoldTime ? (parseInt(this.bootstrapParams.longSeatHoldTime) / 60) : 20;
        } else {
          this.seatHoldTime = this.bootstrapParams.seatHoldTime ? (parseInt(this.bootstrapParams.seatHoldTime) / 60) : 20;
        }
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      data => {
        this.selectedAppointments = data;
      });

    this.seatHoldTime = this.bootstrapParams.seatHoldTime ? (parseInt(this.bootstrapParams.seatHoldTime) / 60) : 20;
  }
  /***
  * Timer function to setInterval for 20 mins
  ***/
  startTimer() {
    var timeInMinutes = this.seatHoldTime;
    this.selectedExamDetailService.setTimerSeconds(this.bootstrapParams.seatHoldTime ? parseInt(this.bootstrapParams.seatHoldTime) : (20 * 60));
    var endTime = moment().add(timeInMinutes, 'm');
    var parent = this; // or use bind

    var clock = document.getElementById('timer');
    function updateClock() {
      var t = parent.timeRemaining(endTime);
      parent.selectedExamDetailService.setTimerSeconds(t.totalSeconds);

      // first time when the user sees the timer message
      parent.translate.get('headerComponent.timerMessage', { minutes: parent.pad2(t.minutes), seconds: parent.pad2(t.seconds) }).subscribe(value => {
        clock.innerHTML = parent.timerSpanStart + parent.clockIcon + value + parent.spanEnd;
      });
      // to set the value again on language change
      parent.translate.onLangChange.subscribe(() => {
        parent.translate.get('headerComponent.timerMessage', { minutes: parent.pad2(t.minutes), seconds: parent.pad2(t.seconds) }).subscribe(value => {
          if (clock.innerHTML != '') {
            clock.innerHTML = parent.timerSpanStart + parent.clockIcon + value + parent.spanEnd;
          }
        });
      });

      if (t.total <= 0) {
        parent.expireTimer();
      } else if (timerBroadCastIntervals.indexOf(t.totalSeconds) !== -1) { // Announce time remaining to screen readers at configured intervals
        parent.broadcastNotification(t);
      }
    }

    // Announce timer start to screen readers
    var initialTime = this.timeRemaining(endTime);
    this.broadcastNotification(initialTime);

    updateClock(); // run function once at first to avoid delay
    var timeInterval = setInterval(updateClock, 1000);
    this.selectedExamDetailService.timeInterval = timeInterval;
  }

  private timeRemaining(endTime: any) {
    var t = moment(endTime).diff(moment());
    var seconds = Math.floor((t / 1000) % 60);
    var minutes = Math.floor((t / 1000 / 60) % 60);
    var totalSeconds = (minutes * 60) + seconds;
    return {
      'total': t,
      'totalSeconds': totalSeconds,
      'minutes': minutes,
      'seconds': seconds
    };
  }

  private pad2(number: any) {
    return (number < 10 ? '0' : '') + number;
  }

  public clearClock() {
    clearInterval(this.selectedExamDetailService.timeInterval);
    this.selectedExamDetailService.setTimerSeconds(0);
    var clock = document.getElementById('timer');
    var notify = document.getElementById('timeReader');
    clock.innerHTML = '';
    notify.innerHTML = '';
  }

  public expireTimer(): void {
    // TODO: clear hold on expire, don't allow forward nav without selecting a slot, still display sidebar

    var clock = document.getElementById('timer');
    var notify = document.getElementById('timeReader');
    clearInterval(this.selectedExamDetailService.timeInterval);
    this.selectedExamDetailService.setTimerSeconds(0);

    this.translate.get('headerComponent.timerExpiration').subscribe(value => {
      clock.innerHTML = this.timerSpanStart + this.alertSpanStart + value + this.spanEnd + this.spanEnd;
      notify.innerHTML = value; // Announce timer expiration to screen readers
    });
    this.translate.onLangChange.subscribe(() => {
      this.translate.get('headerComponent.timerExpiration').subscribe(value => {
        if (clock.innerHTML != '') {
          clock.innerHTML = this.timerSpanStart + this.alertSpanStart + value + this.spanEnd + this.spanEnd;
        }
      });
    });

    this.selectedExamDetailService.clearSlotsForTimer();

    this.metricApiService.log(LogLevel.Debug, 'Hold Timer Expired: User Redirected to slots page.');
    // TODO: Need to setup something in Routing service - MM
    if (this.selectedExamDetailService.currentTargetFlow === TargetFlow.Schedule) {
      if (this.selectedExamDetailService.isProctored) {
        this.router.navigate(['/scheduling/scheduleProctor']);
      } else {
        if (this.selectedAppointments &&
          this.selectedAppointments[0].exam.sessions &&
          this.selectedAppointments[0].exam.sessions.length > 1
        ) {
          this.router.navigate(['/scheduling/scheduleMultiday']);
        } else {
          this.router.navigate(['/scheduling/scheduleTestcenter']);
        }
      }
    } else {
      if (this.selectedExamDetailService.isProctored) {
        this.router.navigate(['/scheduling/rescheduleappointment/rescheduleProctor']);
      } else {
        if (this.selectedAppointments &&
          this.selectedAppointments[0].exam.sessions &&
          this.selectedAppointments[0].exam.sessions.length > 1
        ) {
          this.router.navigate(['/scheduling/rescheduleappointment/rescheduleMultiday']);
        } else {
          this.router.navigate(['/scheduling/rescheduleappointment/rescheduleTestcenter']);
        }
      }
    }
  }

  public startSilentTimer(seconds: number) {
    this.selectedExamDetailService.setTimerSeconds(seconds);
    var endTime = moment().add(seconds, 's');
    var parent = this; // or use bind

    function updateClock() {
      var t = parent.timeRemaining(endTime);
      parent.selectedExamDetailService.setTimerSeconds(t.totalSeconds);

      if (t.total <= 0) {
        parent.endSilentTimer();
      }
    }

    updateClock(); // run function once at first to avoid delay
    var timeInterval = setInterval(updateClock, 1000);
    this.selectedExamDetailService.timeInterval = timeInterval;
  }

  public endSilentTimer() {
    clearInterval(this.selectedExamDetailService.timeInterval);
    this.selectedExamDetailService.setTimerSeconds(0);
  }

  private broadcastNotification(time: any) {
    var notify = document.getElementById('timeReader');

    this.translate.get('headerComponent.timerNotify', { minutes: time.minutes, seconds: time.seconds }).subscribe(value => {
      notify.innerHTML = value;
    });

  }
}